// Client-side slip prep — ports v1 prepareSlipFileForUpload / compressImageFile.

export const SLIP_MAX_FILES = 10;
export const SLIP_MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
export const SLIP_MAX_FILE_SIZE_LABEL = 'สูงสุด 8 MB ต่อไฟล์';

/** Common image extensions for slips (phones, cameras, scanners, exports). */
export const SLIP_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'jpe',
  'jfif',
  'pjpeg',
  'pjp',
  'png',
  'apng',
  'gif',
  'webp',
  'bmp',
  'tif',
  'tiff',
  'heic',
  'heif',
  'avif',
  'svg',
  'ico',
  'raw',
  'cr2',
  'nef',
  'arw',
  'dng',
] as const;

export const SLIP_PDF_EXTENSIONS = ['pdf'] as const;

export const SLIP_ACCEPTED_EXTENSIONS = [...SLIP_IMAGE_EXTENSIONS, ...SLIP_PDF_EXTENSIONS];

const SLIP_IMAGE_EXTENSION_PATTERN = new RegExp(
  `\\.(${SLIP_IMAGE_EXTENSIONS.join('|')})(\\?|$)`,
  'i',
);

/** For `<input type="file" accept="...">` */
export const SLIP_FILE_ACCEPT = [
  'image/*',
  'application/pdf',
  ...SLIP_ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

export const SLIP_SUPPORTED_FORMATS_LABEL =
  'JPG, PNG, WEBP, HEIC, GIF, BMP, TIFF, AVIF, SVG, PDF';

export interface SlipReference {
  fileUrl: string;
  fileName?: string;
  mimeType?: string;
  thumbnailUrl?: string;
}

export function getSlipFileExtension(fileName: string): string {
  const match = String(fileName || '')
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

const SLIP_EXTENSION_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jpe: 'image/jpeg',
  jfif: 'image/jpeg',
  pjpeg: 'image/jpeg',
  pjp: 'image/jpeg',
  png: 'image/png',
  apng: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  raw: 'image/x-raw',
  cr2: 'image/x-canon-cr2',
  nef: 'image/x-nikon-nef',
  arw: 'image/x-sony-arw',
  dng: 'image/x-adobe-dng',
  pdf: 'application/pdf',
};

export function guessSlipMimeType(file: File): string {
  const type = String(file.type || '').trim();
  if (type) return type;
  return SLIP_EXTENSION_MIME[getSlipFileExtension(file.name)] || 'application/octet-stream';
}

export function isSlipImageFile(file: File): boolean {
  const type = String(file.type || '').toLowerCase();
  if (type.startsWith('image/')) return true;
  const ext = getSlipFileExtension(file.name);
  return SLIP_IMAGE_EXTENSIONS.includes(ext as (typeof SLIP_IMAGE_EXTENSIONS)[number]);
}

export function isSlipPdfFile(file: File): boolean {
  const type = String(file.type || '').toLowerCase();
  if (type === 'application/pdf') return true;
  return getSlipFileExtension(file.name) === 'pdf';
}

export function isAcceptedSlipFile(file: File): boolean {
  return isSlipImageFile(file) || isSlipPdfFile(file);
}

export function parseSlipReferences(raw: unknown): SlipReference[] {
  const text = String(raw || '').trim();
  if (!text) return [];
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(normalizeSlipReference).filter((item): item is SlipReference => !!item);
    } catch {
      return [];
    }
  }
  return [{ fileUrl: text }];
}

function normalizeSlipReference(item: unknown): SlipReference | null {
  if (typeof item === 'string') {
    const fileUrl = item.trim();
    return fileUrl ? { fileUrl } : null;
  }
  if (!item || typeof item !== 'object') return null;
  const record = item as Record<string, unknown>;
  const fileUrl = String(record.fileUrl || record.url || '').trim();
  if (!fileUrl) return null;
  return {
    fileUrl,
    fileName: String(record.fileName || record.name || '').trim() || undefined,
    mimeType: String(record.mimeType || record.mime || '').trim() || undefined,
    thumbnailUrl: String(record.thumbnailUrl || '').trim() || undefined,
  };
}

export function serializeSlipReferences(items: SlipReference[]): string {
  if (!items.length) return '';
  if (items.length === 1) {
    const only = items[0];
    if (!only.fileName && !only.mimeType && !only.thumbnailUrl) {
      return only.fileUrl;
    }
  }
  return JSON.stringify(items);
}

export function isSlipPdf(ref: SlipReference): boolean {
  const mime = String(ref.mimeType || '').toLowerCase();
  if (mime === 'application/pdf') return true;
  const name = String(ref.fileName || ref.fileUrl || '');
  return /\.pdf(\?|$)/i.test(name);
}

export function slipDisplayName(ref: SlipReference): string {
  if (ref.fileName) return ref.fileName;
  try {
    const path = new URL(ref.fileUrl).pathname;
    const segment = path.split('/').filter(Boolean).pop() || '';
    return decodeURIComponent(segment) || 'slip';
  } catch {
    return 'slip';
  }
}

export function extractDriveFileId(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const idParam = raw.match(/[?&]id=([^&]+)/);
  if (idParam) return idParam[1];
  const pathMatch =
    raw.match(/\/file\/d\/([^/]+)/) ||
    raw.match(/\/d\/([^/]+)/) ||
    raw.match(/\/folders\/([^/?]+)/);
  return pathMatch ? pathMatch[1] : '';
}

/** Drive thumbnail URL works in <img>; uc?export=view often does not. */
export function getSlipPreviewUrl(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.includes('drive.google.com/thumbnail')) return raw;
  if (SLIP_IMAGE_EXTENSION_PATTERN.test(raw)) return raw;
  const fileId = extractDriveFileId(raw);
  if (!fileId) return '';
  return `https://drive.google.com/thumbnail?id=${encodeURIComponent(fileId)}&sz=w1000`;
}

export interface PreparedSlipFile {
  base64: string;
  mimeType: string;
  fileName: string;
}

function readFileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export async function prepareSlipFileForUpload(file: File): Promise<PreparedSlipFile> {
  // Do not decode selected photos into a full-resolution <img>/canvas just to
  // upload them. High-resolution phone images can exhaust browser memory and
  // blank the tab before JavaScript can display an error. Keep the original
  // file and enforce the documented request limit before reading it.
  const maxBytes = SLIP_MAX_FILE_SIZE_BYTES;
  if (!file.size) throw new Error('ไฟล์ว่างหรืออ่านไม่ได้ กรุณาเลือกไฟล์ใหม่');
  if (file.size > maxBytes) {
    throw new Error(`ไฟล์ใหญ่เกิน 8 MB (${(file.size / 1024 / 1024).toFixed(1)} MB) กรุณาลดขนาดรูปหรือเลือก PDF ที่เล็กลง`);
  }

  const mimeType = guessSlipMimeType(file);
  return {
    base64: await readFileAsBase64(file),
    mimeType,
    fileName: file.name,
  };
}
